// Grid maths for the palette picker. Pure (no pixi), so the awkward cases -- a palette of two, a
// palette of seventy-six, a box too short for a single row -- are unit-tested rather than eyeballed.

export interface GridSpec {
    count: number
    // Box the grid has to live in.
    width: number
    height: number
    cellHeight: number
    // Cells never go narrower than this; the column count is chosen to respect it.
    minCellWidth: number
    // ...and never wider, so a palette of three doesn't stretch into three banners.
    maxCellWidth?: number
    gapX?: number
    gapY?: number
}

export interface GridLayout {
    cols: number
    // Rows the box can hold (the page size), and the rows the current page actually fills.
    rows: number
    usedRows: number
    cellWidth: number
    perPage: number
    pages: number
    // Height the drawn grid occupies, so a section underneath can be placed against it.
    height: number
}

export const layoutGrid = (spec: GridSpec): GridLayout => {
    const gapX = spec.gapX ?? 6
    const gapY = spec.gapY ?? 6
    const count = Math.max(0, spec.count)

    // Widest grid whose cells still clear minCellWidth.
    let cols = Math.max(1, Math.floor((spec.width + gapX) / (spec.minCellWidth + gapX)))
    // A short palette shouldn't be spread over more columns than it has entries.
    cols = Math.max(1, Math.min(cols, count || 1))

    let cellWidth = (spec.width - (cols - 1) * gapX) / cols
    if (spec.maxCellWidth !== undefined) {
        cellWidth = Math.min(cellWidth, spec.maxCellWidth)
    }

    const rows = Math.max(1, Math.floor((spec.height + gapY) / (spec.cellHeight + gapY)))
    const perPage = cols * rows
    const pages = Math.max(1, Math.ceil(count / perPage))
    const usedRows = Math.max(1, Math.min(rows, Math.ceil(count / cols)))

    return {
        cols,
        rows,
        usedRows,
        cellWidth,
        perPage,
        pages,
        height: usedRows * spec.cellHeight + (usedRows - 1) * gapY,
    }
}

// The slice of options shown on `page` (0-based, clamped).
export const pageSlice = <T>(items: T[], layout: GridLayout, page: number): T[] => {
    const safePage = Math.max(0, Math.min(page, layout.pages - 1))
    const start = safePage * layout.perPage
    return items.slice(start, start + layout.perPage)
}

// Where the option at `index` within a page sits, relative to the grid's top-left.
export const cellPosition = (
    index: number,
    layout: GridLayout,
    cellHeight: number,
    gapX: number = 6,
    gapY: number = 6,
): { x: number; y: number } => ({
    x: (index % layout.cols) * (layout.cellWidth + gapX),
    y: Math.floor(index / layout.cols) * (cellHeight + gapY),
})
